/**
 * ProfileLibraryStrip — horizontal preview of a user's most recent media
 * library items, shown above the posts grid on /u/:username and /profile.
 *
 * Auto-hides when there are no visible items (or the library is private to
 * the viewer). Owners always get a "Open library" CTA so empty state is
 * actionable.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { Folder, Image as ImageIcon, Film, Music, FileText, ChevronRight, Lock } from 'lucide-react';

const KIND_ICON = { image: ImageIcon, video: Film, audio: Music, document: FileText, other: Folder };

function fmtBytes(n) {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = num / 1024; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function Thumb({ item }) {
  const ct = item.content_type || '';
  if (ct.startsWith('image/')) {
    return <img src={fileUrl(item.id)} alt="" className="h-full w-full object-cover" loading="lazy" />;
  }
  if (ct.startsWith('video/')) {
    return <video src={fileUrl(item.id)} muted playsInline preload="metadata" className="h-full w-full object-cover" />;
  }
  const Icon = KIND_ICON[item.kind] || Folder;
  return (
    <div className="grid h-full w-full place-items-center bg-[hsl(var(--app-card-2))]">
      <Icon className="h-7 w-7 text-[hsl(var(--muted-foreground))]" />
    </div>
  );
}

export default function ProfileLibraryStrip({ username, isOwner }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [visibility, setVisibility] = useState('public');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!username) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const url = isOwner ? '/me/media' : `/users/${username}/media`;
        const { data } = await api.get(url, { params: { kind: 'all', limit: 8 } });
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setVisibility(data.visibility || 'public');
      } catch {
        if (!cancelled) { setItems([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [username, isOwner]);

  // For visitors: hide entirely when library is private OR there are no items.
  if (!loaded) return null;
  if (!isOwner && (visibility === 'private' || items.length === 0)) return null;

  const libraryHref = isOwner ? '/library' : `/u/${username}/library`;

  return (
    <section
      className="mb-3 rounded-3xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-3 shadow-[var(--shadow-elev-1)]"
      data-testid="profile-library-strip"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Folder className="h-4 w-4 feed-accent-text" />
          <h3 className="font-display text-sm font-medium tracking-tight">
            Library
          </h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]" data-testid="profile-library-strip-count">
            {total} {total === 1 ? 'item' : 'items'}
          </span>
          {isOwner && visibility === 'private' && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]" title="Only you can see this library">
              <Lock className="h-3 w-3" /> private
            </span>
          )}
        </div>
        <Link
          to={libraryHref}
          className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] px-2 py-1 text-xs hover:feed-accent-border"
          data-testid="profile-library-strip-open"
        >
          Open library <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="mt-2 font-mono text-[11px] text-[hsl(var(--muted-foreground))]">
          {isOwner
            ? 'No items yet — anything you upload via the composer or profile lands here.'
            : 'No public items.'}
        </div>
      ) : (
        <div className="mt-2 -mx-1 flex gap-2 overflow-x-auto pb-1" data-testid="profile-library-strip-row">
          {items.map((it) => (
            <Link
              key={it.id}
              to={libraryHref}
              className="group block shrink-0"
              title={`${it.original_filename || it.id} · ${fmtBytes(it.size)}`}
              data-testid="profile-library-strip-item"
            >
              <div className="relative h-20 w-20 overflow-hidden rounded-xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] transition-shadow group-hover:shadow-[var(--shadow-elev-1)]">
                <Thumb item={it} />
                {it.is_private && (
                  <span className="absolute left-1 top-1 inline-flex items-center rounded-full bg-black/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-white">
                    <Lock className="h-2.5 w-2.5" />
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
